import { registerMutation } from '@adornis/baseql/metadata/register.js';
import { DateTime } from 'luxon';
import { resolveDeliveryDateForDebitor } from '../../delivery-date/server/resolveDeliveryDateForDebitor.js';
import { BERLIN } from '../../delivery-date/working-days.js';
import { getMailExtractionResult } from '../../information-extraction/api/getMailExtractionResult.js';
import { MailExtractionResult } from '../../information-extraction/db/MailExtractionResult.js';
import { resolveEffectiveProducts, SapValidationError, validateExtractionForSap } from '../sap-validation.js';
import { ensureCurrentPlannedDate, resolveExportDeliveryDates } from './delivery-dates.js';

export const generateXmlByMail = registerMutation({
  type: () => String,
  operationName: 'generateXmlByMail',
  resolve: mailID => {
    return async () => {
      const extractionResult = await getMailExtractionResult(mailID)(MailExtractionResult.allFields);
      // check if extractionResult exists
      if (!extractionResult) throw new Error('No extraction result found for mailID: ' + mailID);
      // XML-Generierung
      const xml = await generateXML(extractionResult);
      return xml;
    };
  },
  params: [{ name: 'mailID', type: () => String }],
});

export async function generateXML(extractionResult: MailExtractionResult): Promise<string> {
  // Gate: never produce an offer XML when mandatory SAP fields are missing.
  const report = validateExtractionForSap(extractionResult);
  if (!report.ok) throw new SapValidationError(report);

  const xmlHeader = '<?xml version="1.0" encoding="UTF-8"?>';

  const quotationsStart =
    '<QUOTATIONS xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" xsi:noNamespaceSchemaLocation="ki_quatation.xsd">';
  const quotationsEnd = '</QUOTATIONS>';

  // SAP will `yyyyMMdd`. Die Termine liegen bereits als `yyyy-MM-dd` vor — ein Datum ohne
  // Uhrzeit, das keine Zeitzone verrutschen lassen kann.
  const formatDate = (day: string) => day.replaceAll('-', '');

  // Das Wunschlieferdatum kommt aus dem Routenfahrplan (siehe features/delivery-date). Der
  // Termin, den die Extraktion errechnet hat, steht am Ergebnis; er wird hier nur dann neu
  // bestimmt, wenn er fehlt oder inzwischen verstrichen ist — etwa bei einem Angebot, das erst
  // Tage später freigegeben oder von Hand erneut gesendet wird. Bezugspunkt ist auch dann das
  // Jetzt, nie der Eingang der Mail.
  const now = DateTime.now().setZone(BERLIN);
  const today = now.toFormat('yyyy-MM-dd');
  // Schreibt einen neu gerechneten Termin ans Ergebnis zurück; sendXmlByMail speichert direkt
  // nach dem Versand.
  const plannedDate = await ensureCurrentPlannedDate(extractionResult, today, () =>
    resolveDeliveryDateForDebitor(extractionResult.requestingCompany?.debitor, now),
  );

  const { headerDate, dateForPosition } = resolveExportDeliveryDates(extractionResult, plannedDate);

  const kopf = `
	<KOPF>
		<PURCH_NO_C>${escapeXml(extractionResult.getRequestID())}</PURCH_NO_C>
		<REQ_DATE_H>${escapeXml(formatDate(headerDate))}</REQ_DATE_H>
		<PURCH_DATE>${escapeXml('')}</PURCH_DATE>
		<DOC_TYPE>${escapeXml('ZAG')}</DOC_TYPE>
		<SALES_ORG>${escapeXml('1510')}</SALES_ORG>
		<DISTR_CHAN>${escapeXml('01')}</DISTR_CHAN>
		<DIVISION>${escapeXml('01')}</DIVISION>
		<REF_1>${escapeXml(extractionResult.reference ?? '')}</REF_1>
		<NAME>${escapeXml(extractionResult.requestingCompany?.name ?? '')}</NAME>
		<AG_PARTN>${escapeXml(extractionResult.requestingCompany?.debitor ?? '')}</AG_PARTN>
		<AP_PARTN>${escapeXml(extractionResult.requestingCompany?.partnerID ?? '')}</AP_PARTN>
	</KOPF>
  `;

  const {
    allProducts,
    explicitOrdered: explicitOrderedProducts,
    alternatives: alternativeProducts,
  } = resolveEffectiveProducts(extractionResult);

  // Build the ordered list of positions that go into the XML. Main positions keep
  // their order; a main position without a material number is replaced by a
  // material-bearing alternative. Every remaining alternative is exported as its own
  // regular SAP position right after the main position it belongs to.
  const consumedAlternativeIds = new Set<string>();
  const exportProducts: typeof allProducts = [];

  for (const product of explicitOrderedProducts) {
    if (product.productNumber) {
      exportProducts.push(product);
    } else {
      // Safe after the validation above: a material-bearing alternative must exist for
      // unmatched main products. Match the validator's selection (requires a productNumber)
      // so generation and validation never pick a different alternative.
      const substitute = alternativeProducts.find(a => a.alternativeFor === product.positionNumber && a.productNumber)!;
      exportProducts.push(substitute);
      consumedAlternativeIds.add(substitute._id);
    }

    // Append this position's other (material-bearing) alternatives as regular positions.
    for (const alt of alternativeProducts) {
      if (alt.alternativeFor === product.positionNumber && alt.productNumber && !consumedAlternativeIds.has(alt._id)) {
        exportProducts.push(alt);
        consumedAlternativeIds.add(alt._id);
      }
    }
  }

  // Orphan alternatives (no matching main position) still go into the export.
  for (const alt of alternativeProducts) {
    if (alt.productNumber && !consumedAlternativeIds.has(alt._id)) exportProducts.push(alt);
  }

  const positionen = exportProducts
    .map((product, index) => {
      const isFX = product.lengthType === 'FX';

      // Die Grenzen stehen seit 26.08.2026 in der Position selbst — resolveLengthWindow() setzt
      // sie in der Extraktion, damit im Frontend vor dem Absenden steht, was hochgeht. Hier bleibt
      // nur die Formatumrechnung: TOL_1/TOL_2 sind bei einer Fixlänge Abweichungen von der
      // Nennlänge, bei einer Handelslänge absolute Grenzen.
      const tol1 = isFX ? Math.abs((product.minLength ?? 0) - (product.length ?? 0)) : (product.minLength ?? 0);
      const tol2 = isFX ? Math.abs((product.maxLength ?? 0) - (product.length ?? 0)) : (product.maxLength ?? 0);

      return `
		<POSITIONEN>
			<ITM_NUMBER>${escapeXml((index + 1) * 10)}</ITM_NUMBER>
			<PO_ITM_NO>${escapeXml((index + 1) * 10)}</PO_ITM_NO>
			<MATERIAL>${escapeXml(product.productNumber ?? '')}</MATERIAL>
			<CUST_MAT22></CUST_MAT22>
			<PLANT></PLANT>
			<TARGET_QTY>${escapeXml(
        isFX
          ? (product.quantity ?? 0)
          : (product.quantity ?? 0) *
              // length is in mm, we need to convert it to meters
              // thats why we divide by 1000
              ((product.length ?? 0) / 1000),
      )}</TARGET_QTY>
			<TARGET_QU>${escapeXml(isFX ? 'ST' : 'M')}</TARGET_QU>
			<T_UNIT_ISO>${escapeXml(isFX ? 'ST' : 'M')}</T_UNIT_ISO>
			<ITEM_CATEG>${escapeXml('AGN')}</ITEM_CATEG>
			<PURCH_NO_C>${escapeXml(product.requestID ?? '')}</PURCH_NO_C>
			<ZEUGNIS_ART>${escapeXml(product.certificate ?? '')}</ZEUGNIS_ART>
			<MASS>${escapeXml(product.length ?? 0)}</MASS>
			<TOL_1>${escapeXml(tol1)}</TOL_1>
			<TOL_2>${escapeXml(tol2)}</TOL_2>
			<LART>${escapeXml(product.lengthType)}</LART>
      <SALES_UNIT>${escapeXml(isFX ? 'ST' : 'M')}</SALES_UNIT>
      <S_UNIT_ISO>${escapeXml(isFX ? 'ST' : 'M')}</S_UNIT_ISO>
			<EINTEILUNGEN>
				<SCHED_LINE>${escapeXml('1')}</SCHED_LINE>
				<REQ_DATE>${escapeXml(formatDate(dateForPosition(product)))}</REQ_DATE>
				<REQ_QTY>${escapeXml(
          isFX
            ? (product.quantity ?? 0)
            : (product.quantity ?? 0) *
                // length is in mm, we need to convert it to meters
                // thats why we divide by 1000
                ((product.length ?? 0) / 1000),
        )}</REQ_QTY>
				<SCHED_TYPE>${escapeXml('ZA')}</SCHED_TYPE>
			</EINTEILUNGEN>
		</POSITIONEN>`;
    })
    .join('\n');

  const quotation = `
	<QUOTATION>${kopf}${positionen}
	</QUOTATION>`;

  return `${xmlHeader}
${quotationsStart}${quotation}
${quotationsEnd}`;
}

function escapeXml(input: any): string {
  if (!input || typeof input !== 'string') return input;
  return input
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}
